import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Filter, Check, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

export interface AddressFilterValue {
  value: string;
  label: string;
  type: 'country' | 'region' | 'department' | 'city';
}

interface AddressColumnFilterProps {
  values: AddressFilterValue[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  className?: string;
}

interface CategoryState {
  country: boolean;
  region: boolean;
  department: boolean;
  city: boolean;
}

export function AddressColumnFilter({
  values,
  selectedValues,
  onChange,
  className,
}: AddressColumnFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<CategoryState>({
    country: false,
    region: false,
    department: false,
    city: false,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasActiveFilter = selectedValues.length > 0;

  // Group values by type
  const groupedValues = useMemo(() => {
    const groups = {
      country: [] as AddressFilterValue[],
      region: [] as AddressFilterValue[],
      department: [] as AddressFilterValue[],
      city: [] as AddressFilterValue[],
    };
    values.forEach((v) => {
      if (v.type in groups) {
        groups[v.type].push(v);
      }
    });
    return groups;
  }, [values]);

  // Filter values by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedValues;
    const lower = searchTerm.toLowerCase();
    return {
      country: groupedValues.country.filter(
        (v) => v.label.toLowerCase().includes(lower) || v.value.toLowerCase().includes(lower)
      ),
      region: groupedValues.region.filter(
        (v) => v.label.toLowerCase().includes(lower) || v.value.toLowerCase().includes(lower)
      ),
      department: groupedValues.department.filter(
        (v) => v.label.toLowerCase().includes(lower) || v.value.toLowerCase().includes(lower)
      ),
      city: groupedValues.city.filter(
        (v) => v.label.toLowerCase().includes(lower) || v.value.toLowerCase().includes(lower)
      ),
    };
  }, [groupedValues, searchTerm]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    const maxH = viewportHeight - top - 8;
    setDropdownPosition({ top, left });
    dropdownRef.current.style.maxHeight = `${Math.max(maxH, 120)}px`;
    setIsPositioned(true);
  }, []);

  // Position dropdown when opened
  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false);
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition]);

  // Reposition after category expand/collapse changes dropdown height
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(updatePosition);
    }
  }, [expandedCategories, updatePosition]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const toggleCategory = (category: keyof CategoryState) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const getCategoryLabel = (category: keyof CategoryState): string => {
    switch (category) {
      case 'country':
        return 'Country';
      case 'region':
        return 'Region';
      case 'department':
        return 'Department';
      case 'city':
        return 'City';
      default:
        return category;
    }
  };

  const getSelectedCountForCategory = (category: keyof CategoryState): number => {
    return groupedValues[category].filter((v) => selectedValues.includes(v.value)).length;
  };

  const renderCategory = (category: keyof CategoryState, items: AddressFilterValue[]) => {
    if (items.length === 0) return null;

    const isExpanded = expandedCategories[category];
    const selectedCount = getSelectedCountForCategory(category);

    return (
      <div key={category} className="border-b border-dark-border last:border-b-0">
        <button
          onClick={() => toggleCategory(category)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-dark-text-secondary hover:bg-dark-surface transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          <span className="flex-1 text-left">{getCategoryLabel(category)}</span>
          {selectedCount > 0 && (
            <span className="text-primary-400 text-xs">({selectedCount})</span>
          )}
          <span className="text-dark-text-muted text-xs">{items.length}</span>
        </button>
        {isExpanded && (
          <div className="pb-1">
            {items.map((item) => {
              const isSelected = selectedValues.includes(item.value);
              return (
                <button
                  key={item.value}
                  onClick={() => handleToggle(item.value)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 pl-7 py-1 text-xs text-left transition-colors',
                    isSelected
                      ? 'bg-primary-600/20 text-primary-300'
                      : 'text-dark-text-secondary hover:bg-dark-surface'
                  )}
                >
                  <div
                    className={clsx(
                      'w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0',
                      isSelected ? 'bg-primary-500 border-primary-500' : 'border-dark-border'
                    )}
                  >
                    {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="flex-1 truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const hasAnyValues =
    filteredGroups.country.length > 0 ||
    filteredGroups.region.length > 0 ||
    filteredGroups.department.length > 0 ||
    filteredGroups.city.length > 0;

  return (
    <div className={clsx('relative inline-flex', className)}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={clsx(
          'p-0.5 rounded-sm transition-colors',
          hasActiveFilter
            ? 'text-primary-400 hover:text-primary-300'
            : 'text-dark-text-muted hover:text-dark-text-secondary opacity-0 group-hover:opacity-100'
        )}
        title="Filter column"
      >
        <Filter className="w-3.5 h-3.5" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={clsx(
              'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[240px] max-w-[320px] flex flex-col overflow-hidden',
              isPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
            )}
            style={isPositioned
              ? { top: dropdownPosition.top, left: dropdownPosition.left }
              : { top: -9999, left: -9999 }
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search input */}
            <div className="p-2 border-b border-dark-border">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search locations..."
                className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:border-primary-500"
              />
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2 px-2 py-1.5 border-b border-dark-border text-xs">
              <button
                onClick={handleClearAll}
                className="text-dark-text-secondary hover:text-dark-text-primary transition-colors"
              >
                Clear all
              </button>
              {hasActiveFilter && (
                <>
                  <span className="text-dark-text-muted">|</span>
                  <span className="text-dark-text-muted">{selectedValues.length} selected</span>
                </>
              )}
            </div>

            {/* Grouped values list */}
            <div className="flex-1 overflow-y-auto">
              {!hasAnyValues ? (
                <div className="px-3 py-4 text-sm text-dark-text-muted text-center">
                  No matching locations
                </div>
              ) : (
                <>
                  {renderCategory('country', filteredGroups.country)}
                  {renderCategory('region', filteredGroups.region)}
                  {renderCategory('department', filteredGroups.department)}
                  {renderCategory('city', filteredGroups.city)}
                </>
              )}
            </div>

            {/* Close footer */}
            <div className="flex items-center justify-end gap-2 px-2 py-2 border-t border-dark-border">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="px-3 py-1 text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
              >
                Close
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
